import { Button, Paper, Stack, TextField, Typography } from '@mui/material';
import React from 'react';
import { Field } from 'src/API';
import { FieldInput, FIELD_TYPES } from 'src/api/process-module';
import { CanvasModeSelector } from './CanvasModeSelector';
import { CanvasTemplateSelector } from './CanvasTemplateSelector';
import { ExistingCanvasSelector } from './ExistingCanvasSelector';
import { useProcessModuleCanvas } from '../hooks/useProcessModuleCanvas';

interface ProcessModuleInfiniteCanvasFieldProps {
  field: FieldInput & Partial<Field>;
  index: number;
  handleFieldChange: (
    index: number,
    field: Partial<Partial<FieldInput & Partial<Field>> & { moduleId: string }>,
  ) => void;
  isSubmitting: boolean;
  isUsingExistingField: boolean;
  moduleId: string;
  onSelectExistingField?: () => void;
  allFormFields?: (FieldInput & Partial<Field>)[];
}

export const ProcessModuleInfiniteCanvasField = ({
  field,
  index,
  handleFieldChange,
  isSubmitting,
  moduleId,
  allFormFields,
}: ProcessModuleInfiniteCanvasFieldProps) => {
  // Use custom hook for all canvas logic
  const canvas = useProcessModuleCanvas(field, moduleId, (updates) => handleFieldChange(index, updates), allFormFields);

  // Early return for wrong field type
  if (field.type !== FIELD_TYPES.PROCESS_MODULE_INFINITE_CANVAS) {
    return null;
  }

  // Handle template selection
  const handleTemplateChange = (templateId: string) => {
    canvas.updateCanvasProperties({ templateId });
  };

  // Handle button text change - store in override.label (separate from field label)
  const handleButtonTextChange = (buttonText: string) => {
    canvas.updateCanvasProperties({
      override: {
        ...canvas.properties.override,
        label: buttonText,
      },
    });
  };

  // Get the current button text for display in the TextField
  // Button text comes from override.label, field label is separate
  const currentButtonText = canvas.properties.override?.label ?? 'Open Canvas';

  // Check if canvas is already configured
  const isCanvasConfigured = canvas.properties.canvasId && canvas.properties.canvasCreated;

  return (
    <Stack spacing={3}>
      <Typography variant="h6">Canvas Configuration</Typography>

      {/* Show canvas info if already configured */}
      {isCanvasConfigured && (
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Stack spacing={2}>
            <Stack spacing={1}>
              <Typography variant="body2" color="success.dark" fontWeight="bold">
                ✓ Canvas Configured {canvas.state.selectedExistingCanvas ? '(Using Existing)' : '(New Canvas)'}
              </Typography>
              {/* Show which field this canvas is from if using existing */}
              {canvas.state.selectedExistingCanvas && (
                <>
                  <Typography variant="body2" color="text.secondary">
                    From field: <strong>{canvas.state.selectedExistingCanvas.fieldLabel}</strong>
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    In form: <strong>{canvas.state.selectedExistingCanvas.formName}</strong>
                  </Typography>
                </>
              )}
              {/* Show template if it's a new canvas */}
              {!canvas.state.selectedExistingCanvas && (
                <Typography variant="body2" color="text.secondary">
                  Template: <strong>{canvas.properties.templateId || 'None selected'}</strong>
                </Typography>
              )}
            </Stack>
            <Button
              variant="outlined"
              size="small"
              color="warning"
              onClick={() => {
                canvas.updateCanvasProperties({
                  canvasId: '',
                  canvasCreated: false,
                  templateId: '',
                });
                canvas.setCanvasMode('new');
                // Note: setCanvasMode already clears selectedExistingCanvas
              }}
              disabled={isSubmitting}
              sx={{ alignSelf: 'flex-start' }}
            >
              Reset Canvas Configuration
            </Button>
          </Stack>
        </Paper>
      )}

      {/* Only show mode selection and canvas options if canvas is NOT configured */}
      {!isCanvasConfigured && (
        <>
          {/* Canvas mode selection */}
          <CanvasModeSelector
            mode={canvas.state.mode}
            hasAvailableCanvases={canvas.hasAvailableCanvases}
            isSubmitting={isSubmitting}
            onModeChange={canvas.setCanvasMode}
          />

          {/* Existing canvas selection */}
          {canvas.state.mode === 'existing' && (
            <Paper variant="outlined" sx={{ p: 2 }}>
              <ExistingCanvasSelector
                currentCanvasId={canvas.properties.canvasId}
                existingFields={canvas.state.existingFields}
                isSubmitting={isSubmitting}
                onCanvasSelect={canvas.selectExistingCanvas}
              />
            </Paper>
          )}

          {/* New canvas creation */}
          {canvas.state.mode === 'new' && (
            <Paper variant="outlined" sx={{ p: 2 }}>
              <CanvasTemplateSelector
                templateId={canvas.properties.templateId}
                canvasCreated={canvas.properties.canvasCreated}
                isCreating={false}
                isSubmitting={isSubmitting}
                canCreateCanvas={canvas.canCreateCanvas}
                onTemplateChange={handleTemplateChange}
                onCreateCanvas={canvas.configureCanvas}
              />
            </Paper>
          )}
        </>
      )}

      {/* Button text configuration */}
      <TextField
        fullWidth
        label="Button Text"
        value={currentButtonText}
        onChange={(e) => handleButtonTextChange(e.target.value)}
        disabled={isSubmitting}
        helperText="Text displayed on the button that opens the canvas"
        placeholder={field.label || 'Open Canvas'}
      />

      {/* Error display */}
      {canvas.state.error && (
        <Typography color="error" variant="body2">
          {canvas.state.error}
        </Typography>
      )}
    </Stack>
  );
};
